import React, { useRef } from 'react';
import { Download, Printer } from 'lucide-react';
import html2pdf from 'html2pdf.js';
import { useLanguage } from '../context/LanguageContext';

const GaneshUtsavNocForm = () => {
  const { language } = useLanguage();
  const componentRef = useRef();

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPdf = () => {
    const element = componentRef.current;
    const opt = {
      margin: 0,
      filename: language === 'marathi' ? 'Ganesh_Utsav_NOC_2025.pdf' : 'Ganesh_Utsav_NOC_2025_EN.pdf',
      image: { type: 'jpeg', quality: 1 },
      html2canvas: { scale: 3, useCORS: true, logging: false },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };
    html2pdf().from(element).set(opt).save();
  };

  return (
    <div className="min-h-screen bg-gray-200 py-8 font-sans print:py-0 print:bg-white flex flex-col items-center">
      <style>
        {`
          @media print {
            @page {
              size: A4 portrait;
              margin: 0mm;
            }
            body {
              background-color: white !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
          }
        `}
      </style>
      
      {/* Controls Container */}
      <div className="w-full max-w-[210mm] mb-6 flex justify-end space-x-4 print:hidden px-4">
        <button
          onClick={handlePrint}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 shadow-sm transition-colors"
        >
          <Printer className="w-4 h-4 mr-2" />
          {language === 'marathi' ? 'Print' : 'Print'}
        </button>
        <button
          onClick={handleDownloadPdf}
          className="flex items-center px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 shadow-sm transition-colors"
        >
          <Download className="w-4 h-4 mr-2" />
          {language === 'marathi' ? 'Download PDF' : 'Download PDF'}
        </button>
      </div>

      <div className="w-full overflow-x-auto flex justify-center">
        <div ref={componentRef} className="bg-white text-black shadow-lg print:shadow-none relative" style={{ width: '210mm' }}>
          
          {/* === PAGE 1 === */}
          <div className="px-10 py-10 mx-auto relative box-border flex flex-col" style={{ width: '210mm', minHeight: '297mm', maxHeight: '297mm', overflow: 'hidden' }}>
            
            <div className="flex justify-center mb-6">
               <div className="bg-[#b34b4b] text-white px-16 py-2 rounded-lg shadow-md border-2 border-red-100">
                  <h2 className="text-xl">{language === 'marathi' ? 'सार्वजनिक गणेश उत्सव- २०२५' : 'Public Ganesh Utsav- 2025'}</h2>
               </div>
            </div>

            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold border-b-2 border-black inline-block pb-1">
                {language === 'marathi' ? 'ना हरकत प्रमाणपत्रासाठी करावयाचा अर्ज' : 'Application for No Objection Certificate'}
              </h1>
            </div>

            <div className="flex justify-between items-end mb-4 text-[15px]">
              <p>{language === 'marathi' ? 'प्रति,' : 'To,'}</p>
              <p className="flex items-end">
                <span className="mr-2">{language === 'marathi' ? 'दिनांक:' : 'Date:'}</span>
                <span className="border-b border-dashed border-black w-8 inline-block"></span>
                <span className="mx-1">/</span>
                <span className="border-b border-dashed border-black w-16 inline-block"></span>
                <span className="ml-1">{language === 'marathi' ? '/२०२५' : '/2025'}</span>
              </p>
            </div>

            <div className="mb-6 text-[15px] leading-loose">
              <p className="font-bold mb-1">{language === 'marathi' ? 'सरपंच / ग्रामपंचायत अधिकारी,' : 'Sarpanch / Gram Panchayat Officer,'}</p>
              
              <p className="flex items-end mb-1">
                <span className="shrink-0 mr-1">{language === 'marathi' ? 'ग्रामपंचायत कार्यालय' : 'Gram Panchayat Office'}</span>
                <span className="border-b border-dashed border-black w-64"></span>
              </p>
              <p className="flex items-end">
                <span className="shrink-0 mr-1">{language === 'marathi' ? 'ता.' : 'Tal.'}</span>
                <span className="border-b border-dashed border-black w-48"></span>
                <span className="shrink-0 mx-2">{language === 'marathi' ? 'जि.' : 'Dist.'}</span>
                <span className="border-b border-dashed border-black w-48"></span>
              </p>
            </div>

            <div className="text-center mb-6 text-[16px] font-bold">
               <p>{language === 'marathi' ? 'विषय :- सार्वजनिक गणेश उत्सव २०२५ - ना हरकत प्रमाणपत्र मिळणेबाबत' : 'Subject :- Public Ganesh Utsav 2025 - Regarding obtaining No Objection Certificate'}</p>
            </div>

            <div className="pl-12 mb-6 text-[15px] leading-loose">
               <p className="flex items-end mb-1">
                 <span className="shrink-0 font-bold mr-2">{language === 'marathi' ? 'अर्जदार :-' : 'Applicant :-'}</span>
                 <span className="shrink-0 mr-1">{language === 'marathi' ? 'श्री. / सौ.' : 'Mr. / Mrs.'}</span>
                 <span className="border-b border-dashed border-black flex-grow"></span>
               </p>
               <p className="flex items-end pl-16 mb-1">
                 <span className="shrink-0 mr-2">{language === 'marathi' ? 'रा.' : 'Res.'}</span>
                 <span className="border-b border-dashed border-black w-64"></span>
                 <span className="shrink-0 mx-2">{language === 'marathi' ? 'मो. नं.' : 'Mob. No.'}</span>
                 <span className="border-b border-dashed border-black flex-grow"></span>
               </p>
               <p className="flex items-end pl-10 mt-3">
                 <span className="shrink-0 mr-2">{language === 'marathi' ? 'अध्यक्ष/सचिव' : 'President/Secretary'}</span>
                 <span className="border-b border-dashed border-black flex-grow"></span>
                 <span className="shrink-0 mx-1">{language === 'marathi' ? 'सार्वजनिक गणेश मंडळ' : 'Public Ganesh Mandal'}</span>
                 <span className="border-b border-dashed border-black w-24"></span>
               </p>
            </div>

            <div className="mb-6 text-[15px] leading-loose">
              <p className="mb-3">{language === 'marathi' ? 'मा महोदय,' : 'Respected Sir/Madam,'}</p>
              <p className="pl-12 text-justify">
                 {language === 'marathi' ? 'वरील विषयान्वये सविनय अर्ज सादर करतो / करते की, सार्वजनिक गणेश उत्सव-२०२५ साठी' : 'With reference to the above subject, I/we humbly submit this application for Public Ganesh Utsav-2025'}
              </p>
              <div className="flex flex-wrap items-end mt-1">
                 <span className="border-b border-dashed border-black flex-grow"></span>
                 <span className="mx-1">{language === 'marathi' ? 'सार्वजनिक गणेश मंडळ मौजे' : 'Public Ganesh Mandal Village'}</span>
                 <span className="border-b border-dashed border-black w-48"></span>
              </div>
              <div className="flex flex-wrap items-end mt-1">
                 <span className="mr-1">{language === 'marathi' ? 'ता.' : 'Tal.'}</span>
                 <span className="border-b border-dashed border-black w-48"></span>
                 <span className="mx-2">{language === 'marathi' ? 'जि.' : 'Dist.'}</span>
                 <span className="border-b border-dashed border-black w-48"></span>
                 <span className="mx-1">{language === 'marathi' ? 'हे आमचे मंडळ' : 'Our Mandal will'}</span>
                 <span className="border-b border-dashed border-black flex-grow"></span>
              </div>
              <div className="flex flex-wrap items-end mt-1 mb-4">
                 <span className="border-b border-dashed border-black w-48"></span>
                 <span className="ml-1">{language === 'marathi' ? 'या ठिकाणी सार्वजनिक गणेशोत्सव साजरा करणार आहे.' : 'celebrate public Ganeshotsav at this location.'}</span>
              </div>

              <p className="pl-12 text-justify mt-4">
                 {language === 'marathi' ? 'शासनाने व ग्रामपंचायतीने विहीत केलेल्या अटी-शर्ती व मार्गदर्शक सुचनांचे पालन करणे आमच्यावर' : 'I/we are aware that it is mandatory for us to follow the terms, conditions, and guidelines prescribed'}
              </p>
              <p className="text-justify mt-1">
                 {language === 'marathi' ? 'बंधनकारक राहील याची मला/आम्हांला जाणीव आहे. त्याचे उल्लंघन झाल्यास त्यास सर्वस्वी मी/आम्ही' : 'by the Government and Gram Panchayat. In case of violation, I/we will be solely'}
              </p>
              <p className="text-justify mt-1 mb-4">
                 {language === 'marathi' ? 'जबाबदार राहू.' : 'responsible.'}
              </p>

              <p className="pl-12 mt-4">
                 {language === 'marathi' ? 'करिता मला/आम्हांला ना हरकत प्रमाणपत्र देण्यात यावे ही विनंती.' : 'Therefore, it is requested to grant me/us the No Objection Certificate.'}
              </p>
            </div>

            <div className="flex justify-end text-[15px] mt-auto">
               <div className="flex flex-col space-y-6 w-[350px]">
                  <p className="flex items-end">
                    <span className="shrink-0 mr-2">{language === 'marathi' ? 'अर्जदाराची सही :' : 'Applicant\'s Signature :'}</span>
                    <span className="border-b border-dashed border-black flex-grow"></span>
                  </p>
                  <p className="flex items-end">
                    <span className="shrink-0 mr-2">{language === 'marathi' ? 'नाव :' : 'Name :'}</span>
                    <span className="border-b border-dashed border-black flex-grow"></span>
                  </p>
               </div>
            </div>

            <div className="mt-8 text-[15px] pb-4">
               <p>{language === 'marathi' ? 'सोबत:' : 'Enclosure:'}</p>
            </div>

          </div>

          <div className="html2pdf__page-break"></div>

          {/* === PAGE 2 === */}
          <div className="px-10 py-10 mx-auto relative box-border flex flex-col" style={{ width: '210mm', minHeight: '297mm', maxHeight: '297mm', overflow: 'hidden' }}>
            
            <div className="flex justify-end mb-6 text-[15px]">
               <div className="flex flex-col space-y-1 w-[280px]">
                 <p className="flex items-end">
                   <span className="shrink-0 mr-1">{language === 'marathi' ? 'कार्यालय ग्रा.पं.' : 'Office G.P.'}</span>
                   <span className="border-b border-dashed border-black flex-grow"></span>
                 </p>
                 <p className="flex items-end">
                   <span className="shrink-0 mr-1">{language === 'marathi' ? 'ता.' : 'Tal.'}</span>
                   <span className="border-b border-dashed border-black w-24"></span>
                   <span className="shrink-0 mx-1">{language === 'marathi' ? 'जि.' : 'Dist.'}</span>
                   <span className="border-b border-dashed border-black flex-grow"></span>
                 </p>
                 <p className="flex items-end font-bold">
                   <span className="mr-2">{language === 'marathi' ? 'दिनांक :' : 'Date :'}</span>
                   <span className="border-b border-dashed border-black w-8 inline-block font-normal"></span>
                   <span className="mx-1 font-normal">/</span>
                   <span className="border-b border-dashed border-black w-16 inline-block font-normal"></span>
                   <span className="ml-1">{language === 'marathi' ? '/२०२५' : '/2025'}</span>
                 </p>
               </div>
            </div>

            <div className="text-center mb-8">
               <h1 className="text-2xl font-bold border-b-2 border-black inline-block pb-1">
                 {language === 'marathi' ? 'ना हरकत प्रमाणपत्र' : 'No Objection Certificate'}
               </h1>
            </div>

            <div className="mb-6 text-[15px] leading-loose">
               <div className="flex flex-wrap items-end">
                  <span className="pl-12 mr-1">{language === 'marathi' ? 'प्रमाणपत्र देण्यात येते की, अर्जदार श्री.' : 'This is to certify that the applicant Mr.'}</span>
                  <span className="border-b border-dashed border-black flex-grow"></span>
               </div>
               <div className="flex flex-wrap items-end mt-1">
                  <span className="mr-1">{language === 'marathi' ? 'अध्यक्ष/सचिव' : 'President/Secretary'}</span>
                  <span className="border-b border-dashed border-black flex-grow"></span>
                  <span className="ml-1">{language === 'marathi' ? 'गणेश मंडळ' : 'Ganesh Mandal'}</span>
               </div>
               <div className="flex flex-wrap items-end mt-4">
                  <span className="mr-1">{language === 'marathi' ? 'मौजे' : 'Village'}</span>
                  <span className="border-b border-dashed border-black w-64"></span>
                  <span className="mx-1">{language === 'marathi' ? 'ता.' : 'Tal.'}</span>
                  <span className="border-b border-dashed border-black flex-grow"></span>
                  <span className="mx-1">{language === 'marathi' ? 'जि.' : 'Dist.'}</span>
                  <span className="border-b border-dashed border-black w-40"></span>
               </div>
               <div className="flex flex-wrap items-end mt-1">
                  <span className="mr-1">{language === 'marathi' ? 'यांच्या अर्जानुसार' : 'as per their application'}</span>
                  <span className="border-b border-dashed border-black flex-grow"></span>
                  <span className="ml-1">{language === 'marathi' ? 'येथे श्री' : 'here Shri'}</span>
               </div>
               <p className="text-justify mt-1">
                  {language === 'marathi' ? 'गणेश उत्सव - २०२५ खालील अटी व शर्तींच्या अधिन राहून साजरा करण्यास ग्रामपंचायत कार्यालयाची काहीही' : 'Ganesh Utsav - 2025 can be celebrated. The Gram Panchayat Office has no objection subject to'}
               </p>
               <p className="text-justify mt-1">
                  {language === 'marathi' ? 'हरकत नाही.' : 'the following terms and conditions.'}
               </p>
            </div>

            <div className="mb-4">
               <h2 className="text-lg font-bold">{language === 'marathi' ? 'अटी व शर्ती :' : 'Terms and Conditions :'}</h2>
            </div>

            <div className="pl-6 mb-6 text-[15px] space-y-1.5 leading-relaxed">
               <div className="flex">
                  <span className="mr-2">{language === 'marathi' ? '१)' : '1)'}</span>
                  <p className="text-justify">{language === 'marathi' ? 'ग्रामपंचायतीने दिलेल्या ना हरकत प्रमाणपत्र व्यतिरिक्त पोलीस विभागाची स्वतंत्र परवानगी घ्यावी लागेल. तसेच आवश्यक संबंधित सर्व वरिष्ठ कार्यालयाची परवानगी घेणे बंधनकारक राहील.' : 'Apart from the Gram Panchayat NOC, separate police permission must be obtained. Also, it is mandatory to get permissions from all concerned higher offices.'}</p>
               </div>
               <div className="flex">
                  <span className="mr-2">{language === 'marathi' ? '२)' : '2)'}</span>
                  <p className="text-justify">{language === 'marathi' ? 'सार्वजनिक आरोग्य, जीवित,सार्वजनिक मालमत्ता, शैक्षणिक संस्था तसेच पर्यावरणाला धोका होणार नाही याची दक्षता घ्यावी.' : 'Care must be taken to not endanger public health, life, public property, educational institutions, and the environment.'}</p>
               </div>
               <div className="flex">
                  <span className="mr-2">{language === 'marathi' ? '३)' : '3)'}</span>
                  <p className="text-justify">{language === 'marathi' ? 'प्लॅस्टिक व थर्माकॉलचा वापर करता येणार नाही, निर्माल्याचे व्यवस्थापन करावे लागेल.' : 'Use of plastic and thermocol is prohibited. Nirmalya (floral offerings) must be properly managed.'}</p>
               </div>
               <div className="flex">
                  <span className="mr-2">{language === 'marathi' ? '४)' : '4)'}</span>
                  <p className="text-justify">{language === 'marathi' ? 'ध्वनीप्रदूषण बाबत शासन नियमांचे पालन करणे बंधनकारक राहील.' : 'It is mandatory to follow government rules regarding noise pollution.'}</p>
               </div>
               <div className="flex">
                  <span className="mr-2">{language === 'marathi' ? '५)' : '5)'}</span>
                  <p className="text-justify">{language === 'marathi' ? 'शक्य असल्यास मंडळाचे परिसरात सीसीटीव्ही कॅमेरे लावावे.' : 'If possible, install CCTV cameras in the Mandal premises.'}</p>
               </div>
               <div className="flex">
                  <span className="mr-2">{language === 'marathi' ? '६)' : '6)'}</span>
                  <p className="text-justify">{language === 'marathi' ? 'वाहतुकीस अडथळा निर्माण होणार नाही याची दक्षता घ्यावी. रहदारी नियमन करण्यासाठी आपले स्वयंसेवक नेमावे.' : 'Ensure no traffic obstruction occurs. Appoint your own volunteers for traffic regulation.'}</p>
               </div>
               <div className="flex">
                  <span className="mr-2">{language === 'marathi' ? '७)' : '7)'}</span>
                  <p className="text-justify">{language === 'marathi' ? 'शासनाने ठरवून दिलेल्या सर्व नियमांचे पालन होईल याची दक्षता घ्यावी.' : 'Care must be taken to ensure all rules laid down by the government are followed.'}</p>
               </div>
               <div className="flex items-end pt-1">
                  <span className="mr-2">{language === 'marathi' ? '८)' : '8)'}</span>
                  <span className="border-b border-dashed border-black flex-grow inline-block mb-1"></span>
               </div>
            </div>

            <div className="mb-8 text-[15px] leading-relaxed">
               <p className="pl-12 text-justify">
                  {language === 'marathi' ? 'ग्रामपंचायतीने नमूद केलेल्या सर्व अटी व शर्तींचे पालन होत नाही असे आढळून आल्यास सदर ना' : 'If it is found that the terms and conditions mentioned by the Gram Panchayat are not followed,'}
               </p>
               <p className="text-justify">
                  {language === 'marathi' ? 'हरकत प्रमाणपत्र रद्द करण्याचे अधिकार ग्रामपंचायतीला राहतील.' : 'the Gram Panchayat reserves the right to cancel this No Objection Certificate.'}
               </p>
               <p className="pl-12 text-justify mt-2">
                  {language === 'marathi' ? 'करिता वरील अटी-शर्तींच्या अधिन राहून ना हरकत प्रमाणपत्र देण्यात येत आहे.' : 'Therefore, this NOC is granted subject to the above terms and conditions.'}
               </p>
               <p className="italic font-bold mt-2">
                  {language === 'marathi' ? 'सर्वांना श्रीगणेश उत्सवाच्या हार्दिक शुभेच्छा...!' : 'Hearty wishes for Shri Ganesh Utsav to all...!'}
               </p>
            </div>

            <div className="flex justify-between items-end mt-auto text-[15px] pb-4">
               <div className="w-1/2 flex justify-center">
                  <p className="font-bold">{language === 'marathi' ? 'ग्रामपंचायत अधिकारी' : 'Gram Panchayat Officer'}</p>
               </div>
               <div className="w-1/2 flex justify-center">
                  <p className="font-bold">{language === 'marathi' ? 'सरपंच' : 'Sarpanch'}</p>
               </div>
            </div>

            <div className="flex items-end justify-center mb-8 text-[15px]">
               <span className="mr-2">{language === 'marathi' ? 'ग्रामपंचायत' : 'Gram Panchayat'}</span>
               <span className="border-b border-dashed border-black w-64"></span>
            </div>

            <div className="absolute bottom-6 left-10 text-blue-700 text-sm underline print:hidden">
              www.majhigrampanchayat.com
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default GaneshUtsavNocForm;
